import type {
  ReportReadiness,
  ReportVersion,
  EvidenceAsset,
  ExpenseAction,
  ExpenseInput,
  ExpenseState,
  Role,
} from "@receipt-ledger/contracts";
export type { EvidenceAsset };
export type Expense = ExpenseInput & {
  id: string;
  state: ExpenseState;
  revision: number;
  creatorUserId: string;
  roles: Role[];
  availableActions: ExpenseAction[];
  /** Reason text from the most recent request-clarification call. Only meaningful when state is "clarification". */
  clarificationReason?: string;
};
export type { ReportReadiness, ReportVersion };
// Empty string = relative/same-origin, which is correct once this is deployed
// as static assets served by the same Worker as the API (see apps/api/wrangler.toml's
// [assets] block). Local dev against a separate API process sets VITE_API_URL.
const base = import.meta.env.VITE_API_URL ?? "";

let accessToken: string | null = null;
let currentUserEmail: string | null = null;
let selectedBusinessId: string | null = null;

/** Called from App.tsx's Supabase auth-state listener. A null session clears the token. */
export function setSession(
  session: { accessToken: string; email: string | null } | null,
): void {
  accessToken = session?.accessToken ?? null;
  currentUserEmail = session?.email ?? null;
}

/** Update the selected business ID for subsequent API calls. */
export function setSelectedBusinessId(businessId: string | null): void {
  selectedBusinessId = businessId;
}

/** Get the current selected business ID. */
export function getSelectedBusinessId(): string | null {
  return selectedBusinessId;
}
const headers = () => ({
  "content-type": "application/json",
  // Only meaningful for local dev against a plain `npm run dev` API (no
  // verifyToken configured there, see apps/api/src/server.ts) - the deployed
  // Worker always requires and trusts the Authorization header instead, see
  // apps/api/src/auth.ts.
  "x-user-id": currentUserEmail ?? "pilot-owner",
  "x-business-id": selectedBusinessId ?? "00000000-0000-4000-8000-000000000001",
  ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
});
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly id: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: { ...headers(), ...init.headers },
  });
  if (!response.ok) {
    const body = (await response.json()) as {
      error?: { id?: string; message?: string; details?: unknown };
    };
    throw new ApiError(
      body.error?.message ?? "Request failed",
      body.error?.id ?? "internal_error",
      body.error?.details,
    );
  }
  return response.json() as Promise<T>;
}
export const api = {
  list: () => request<Expense[]>("/expenses"),
  create: (expense: ExpenseInput) =>
    request<Expense>("/expenses", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify(expense),
    }),
  transition: (expense: Expense, action: "submit" | "confirm" | "archive") =>
    request<Expense>(`/expenses/${expense.id}/${action}`, {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ expectedRevision: expense.revision }),
    }),
  clarify: (expense: Expense, reason: string) =>
    request<Expense>(`/expenses/${expense.id}/request-clarification`, {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ expectedRevision: expense.revision, reason }),
    }),
  restore: (expense: Expense) =>
    request<Expense>(`/expenses/${expense.id}/restore`, {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ expectedRevision: expense.revision }),
    }),
  revise: (expense: Expense, input: ExpenseInput) =>
    request<Expense>(`/expenses/${expense.id}/revise`, {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({
        expectedRevision: expense.revision,
        expense: input,
      }),
    }),
  listBusinesses: () =>
    request<Array<{ id: string; name: string }>>("/businesses"),
  getSettings: () =>
    request<{ autoConfirmEnabled: boolean }>("/businesses/current/settings"),
  updateSettings: (settings: { autoConfirmEnabled: boolean }) =>
    request<{ autoConfirmEnabled: boolean }>("/businesses/current/settings", {
      method: "PATCH",
      body: JSON.stringify(settings),
    }),
  reportReadiness: (taxYear: number) =>
    request<ReportReadiness>(`/reports/readiness/${taxYear}`),
  reports: (taxYear?: number) =>
    request<ReportVersion[]>(`/reports${taxYear ? `?taxYear=${taxYear}` : ""}`),
  generateReport: (taxYear: number) =>
    request<ReportVersion>("/reports/generate", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ taxYear }),
    }),
  listEvidence: (expenseId: string) =>
    request<EvidenceAsset[]>(`/expenses/${expenseId}/evidence`),
  /**
   * The evidence bytes themselves, proxied through the API (not a direct R2
   * URL) so the same Authorization header every other call uses works here
   * too - a plain <img src> can't attach custom headers, so callers fetch
   * this and turn it into an object URL (see EvidenceThumbnail.tsx).
   */
  evidenceContent: async (assetId: string): Promise<Blob> => {
    const response = await fetch(`${base}/evidence/${assetId}/content`, {
      headers: headers(),
    });
    if (!response.ok)
      throw new ApiError(
        "Could not load the receipt image.",
        "evidence_unavailable",
      );
    return response.blob();
  },
  /**
   * Full attach-a-receipt flow in one call: request a signed upload URL,
   * PUT the bytes directly to storage (R2, not through this Worker - see
   * EvidenceUploadIntent), then register it against the expense. Matches
   * the same three-step flow apps/ios's EvidenceQueue.sync() uses.
   */
  attachReceipt: async (
    expense: Expense,
    file: File,
  ): Promise<EvidenceAsset> => {
    if (!SUPPORTED_EVIDENCE_TYPES.has(file.type))
      throw new ApiError(
        "Choose a JPEG, PNG, or PDF file.",
        "content_type_unsupported",
      );
    const bytes = await file.arrayBuffer();
    const sha256 = await sha256Hex(bytes);
    const objectKey = `quarantine/${expense.id}/${crypto.randomUUID()}`;
    const intent = await request<{
      objectKey: string;
      uploadUrl: string;
      headers: Record<string, string>;
    }>("/evidence/upload-intents", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({
        objectKey,
        sha256,
        byteSize: bytes.byteLength,
        contentType: file.type,
      }),
    });
    const uploadResponse = await fetch(intent.uploadUrl, {
      method: "PUT",
      headers: intent.headers,
      body: bytes,
    });
    if (!uploadResponse.ok)
      throw new ApiError(
        "The upload didn't go through. Try again.",
        "evidence_upload_invalid",
      );
    return request<EvidenceAsset>("/evidence", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({
        expenseId: expense.id,
        revision: expense.revision,
        objectKey: intent.objectKey,
        sha256,
        byteSize: bytes.byteLength,
        contentType: file.type,
      }),
    });
  },
};

const SUPPORTED_EVIDENCE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "application/pdf",
]);

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}
